import {useCallback, useState} from 'react'
import {View} from 'react-native'
import {RichText} from '@atproto/api'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useQueryClient} from '@tanstack/react-query'
import {nanoid} from 'nanoid/non-secure'

import * as apilib from '#/lib/api'
import {shortenLinks} from '#/lib/strings/rich-text-manip'
import {useProfileQuery} from '#/state/queries/profile'
import {createPostgateRecord} from '#/state/queries/postgate/util'
import {threadgateRecordToAllowUISetting} from '#/state/queries/threadgate'
import {useAgent, useSession} from '#/state/session'
import {useSimpleVerificationState} from '#/components/verification'
import * as Toast from '#/view/com/util/Toast'
import {atoms as a, useBreakpoints, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import * as TextField from '#/components/forms/TextField'
import {Text} from '#/components/Typography'

export {useDialogControl as useGetVerifiedDialogControl} from '#/components/Dialog'

export function GetVerifiedDialog({
  control,
}: {
  control: Dialog.DialogControlProps
}) {
  return (
    <Dialog.Outer control={control} nativeOptions={{preventExpansion: true}}>
      <Dialog.Handle />
      <GetVerifiedDialogInner control={control} />
    </Dialog.Outer>
  )
}

function GetVerifiedDialogInner({
  control,
}: {
  control: Dialog.DialogControlProps
}) {
  const t = useTheme()
  const {_} = useLingui()
  const {gtMobile} = useBreakpoints()
  const {currentAccount} = useSession()
  const agent = useAgent()
  const queryClient = useQueryClient()
  const {data: profile} = useProfileQuery({did: currentAccount?.did})
  const {isVerified} = useSimpleVerificationState({profile: profile ?? undefined})
  const [customHandle, setCustomHandle] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const onSubmit = useCallback(async () => {
    const handle = customHandle.trim()
    if (!handle) {
      Toast.show(_(msg`Please enter a custom handle`), 'exclamation-circle')
      return
    }
    if (!currentAccount?.handle) return

    setIsSubmitting(true)
    try {
      const postText = `Hey, @shxreadmin.shxre.live, @${currentAccount.handle} has requested a custom handle for @${handle}.`
      const richtext = new RichText({text: postText})
      richtext.detectFacetsWithoutResolution()

      const thread = {
        posts: [
          {
            id: nanoid(),
            richtext,
            shortenedGraphemeLength: shortenLinks(richtext).graphemeLength,
            labels: [],
            embed: {quote: undefined, media: undefined, link: undefined},
          },
        ],
        postgate: createPostgateRecord({post: '', embeddingRules: []}),
        threadgate: threadgateRecordToAllowUISetting({
          $type: 'app.bsky.feed.threadgate',
          post: '',
          createdAt: new Date().toISOString(),
          allow: undefined,
        }),
      }

      await apilib.post(agent, queryClient, {thread})
      Toast.show(_(msg`Verification request sent`), 'check')
      control.close(() => setCustomHandle(''))
    } catch (e: unknown) {
      Toast.show(
        _(msg`Failed to send verification request. Please try again.`),
        'exclamation-circle',
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [
    customHandle,
    currentAccount?.handle,
    agent,
    queryClient,
    control,
    _,
  ])

  if (isVerified) {
    return (
      <Dialog.ScrollableInner
        label={_(msg`Verified`)}
        style={[
          gtMobile ? {width: 'auto', maxWidth: 400, minWidth: 200} : a.w_full,
        ]}>
        <View style={[a.gap_md, a.pb_lg]}>
          <Text style={[a.text_2xl, a.font_semi_bold, a.leading_tight]}>
            <Trans>Verified</Trans>
          </Text>
          <Text
            style={[a.text_md, a.leading_snug, t.atoms.text_contrast_medium]}>
            <Trans>Your account is already verified.</Trans>
          </Text>
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    )
  }

  return (
    <Dialog.ScrollableInner
      label={_(msg`Get Verified`)}
      style={[
        gtMobile ? {width: 'auto', maxWidth: 400, minWidth: 200} : a.w_full,
      ]}>
      <View style={[a.gap_md, a.pb_lg]}>
        <Text style={[a.text_2xl, a.font_semi_bold, a.leading_tight]}>
          <Trans>Get Verified</Trans>
        </Text>
        <Text style={[a.text_md, a.leading_snug, t.atoms.text_contrast_medium]}>
          <Trans>
            Request a custom handle for your account. Enter the handle you would
            like (without @).
          </Trans>
        </Text>
        <View style={[a.pt_sm]}>
          <TextField.Input
            label={_(msg`Custom handle`)}
            placeholder={_(msg`e.g. News`)}
            value={customHandle}
            onChangeText={setCustomHandle}
            autoCapitalize="none"
            autoCorrect={false}
          />
        </View>
        <Button
          label={_(msg`Submit`)}
          size="large"
          variant="solid"
          color="primary"
          onPress={onSubmit}
          disabled={isSubmitting}
          style={[a.mt_md]}>
          <ButtonText>
            {isSubmitting ? (
              <Trans>Sending…</Trans>
            ) : (
              <Trans>Submit</Trans>
            )}
          </ButtonText>
        </Button>
      </View>
      <Dialog.Close />
    </Dialog.ScrollableInner>
  )
}
